import { curry, isNil, propEq } from 'ramda'
import React, { useEffect, useLayoutEffect } from 'react'
import { useLoaderData, useParams } from 'react-router-dom'
import { useQuery } from 'urql'
import ResourceNotFoundError from '../../../errors/ResourceNotFoundError'
import { ResourceBySlugQuery } from '../../../graphql/documents'
import useDisplayError from '../../../hooks/useDisplayError'
import PageNavContainer from '../../../state/PageNavContainer'
import OldAppHeader from '../../AppModule/App/OldAppHeader'
import LoadOverlay from '../../shared/LoadOverlay'
import Resource from './Resource/Resource'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PAGE_ID = `resource`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const propTypenameEquals = curry((typename, o) =>
  propEq(typename, `__typename`, o)
)

const propTypenameEqualsCmsError = propTypenameEquals(`CmsError`)

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const ResourceModule = () => {
  const loaderData = useLoaderData()
  const { displayError } = useDisplayError()

  const [, setNav] = PageNavContainer.useContainer()

  const { id: resourceSlug } = useParams()

  const [result] = useQuery({
    query: ResourceBySlugQuery,
    variables: { resourceSlug },
  })

  const { data, error } = result

  // The API will return a resouce with a value of 'null' if there is no
  // resource matching the id/slug we supplied.
  const resourceNotFound = data && isNil(data.resourceBySlug)
  const resourceHasCmsError =
    data && propTypenameEqualsCmsError(data.resourceBySlug)

  useLayoutEffect(() => {
    setNav({
      pageId: PAGE_ID,
      Header: OldAppHeader,
    })
  }, [setNav])

  // If there is a top-level error, or an inline error, display a modal
  useEffect(() => {
    if (error || resourceHasCmsError) {
      displayError(
        `Couldn’t load resource`,
        error || new Error(`There was a problem loading this resource`)
      )
    }
  }, [error, displayError, resourceHasCmsError, data])

  // Handle a 404 (this will be handled by our Error Page Module)
  useEffect(() => {
    if (resourceNotFound) {
      throw new ResourceNotFoundError(resourceSlug)
    }
  }, [data, resourceNotFound, resourceSlug])

  const showSpinner = resourceHasCmsError || !data?.resourceBySlug || error

  return showSpinner ? (
    <LoadOverlay />
  ) : (
    <Resource
      resource={data.resourceBySlug}
      content={loaderData.page.content}
    />
  )
}

export default ResourceModule
